import { CloseButton, SettingsButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { IDateRange } from '../../../../data/providers/settings/dates/dateRangeProvider';
import {
  blockReportSettingsProvider,
  IBlockReportSettings,
  IBlockReportSettingsState,
} from '../../../../data/providers/settings/reports/blockReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import { isValidDateRange } from '../../../../data/providers/utils/isValidDateRange';
import {
  deleteSelectedTemplate,
  saveBlockSettings,
} from '../../../../data/writers/settings/reports/blockReportSettings';
import { startOfToday } from '../../../../utils/date';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { Toggle } from '../../../shared/form/Toggle';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateSelect } from '../../utils/settings/TemplateSelect';
import { TimeframeSelector } from '../../utils/settings/TimeframeSelector';
import { TemplateForm } from './TemplateForm';

interface IPopupProps {
  onClose: () => void;
  onSave: () => void;
}

export const Popup = (props: IPopupProps): React.ReactElement => {
  const today = startOfToday();
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<ISettingsTemplate<IBlockReportSettings>>();
  const [templates, setTemplates] = React.useState<
    Array<ISettingsTemplate<IBlockReportSettings>>
  >([]);
  const [initialSettings, setInitialSettings] =
    React.useState<ISettingsTemplate<IBlockReportSettings>>();
  // This report will always be a two-date comparison
  const [timeframe, setTimeframe] = React.useState<IDateRange>({
    dateType: {
      label: 'two-date',
      value: 'two-date',
    },
    dateRange: {
      from: {
        label: 'from',
        value: null,
      },
      to: {
        label: 'to',
        value: today,
      },
    },
  });
  const [loading, setLoading] = React.useState<boolean>(true);
  const [maxValueDatapointsToggled, setMaxValueDatapointsToggled] =
    React.useState<boolean>();

  /**
   * @description subscribe to block report settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = blockReportSettingsProvider().subscribe(
      (mSettings: IBlockReportSettingsState): void => {
        setTemplates(mSettings?.templates);
        setSelectedTemplate(mSettings?.selected);
        setInitialSettings(mSettings?.selected);
        setMaxValueDatapointsToggled(mSettings?.isMaxDatapoints);
        /// dont override timeframe unless it actually has a value
        if (!isEmpty(mSettings?.timeframe)) {
          setTimeframe({
            ...timeframe,
            dateRange: mSettings.timeframe.dateRange,
          });
        }
        setLoading(false);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description saves the report settings to database
   */
  const handleSave = async (): Promise<void> => {
    /// make sure there was an actual change
    if (!isEqual(selectedTemplate?.id, initialSettings?.id)) {
      props.onSave();
    }
    setLoading(true);
    await saveBlockSettings(
      selectedTemplate,
      timeframe,
      maxValueDatapointsToggled
    );
    props.onClose();
  };

  /**
   * @description handles change to the selected template
   * @param mSelectedTemplate
   */
  const templateChangeHandler = (
    mSelectedTemplate: ISettingsTemplate<IBlockReportSettings>
  ): void => {
    setSelectedTemplate(mSelectedTemplate);
  };

  /**
   * @description deletes the supplied template and finds a suitable replacement for the selected template
   * @param template
   */
  const templateDeleteHandler = async (
    template: ISettingsTemplate<IBlockReportSettings>
  ): Promise<void> => {
    const index = templates.findIndex(
      (tmp: ISettingsTemplate<IBlockReportSettings>): boolean =>
        tmp.id === template.id
    );
    /// get either the template before or after the template being deleted
    const replacement: ISettingsTemplate<IBlockReportSettings> =
      index === 0 ? templates?.[1] : templates?.[index - 1];

    await deleteSelectedTemplate(template, replacement);
  };

  /**
   * @description handles the change to the timeframe
   * @param range
   */
  const timeframeHandler = (range: IDateRange): void => {
    setTimeframe(
      (prevState: IDateRange): IDateRange => ({
        ...prevState,
        dateRange: {
          from: {
            label: 'from',
            value: range.dateRange.from.value,
          },
          to: {
            label: 'to',
            value: range.dateRange.to.value,
          },
        },
      })
    );
  };

  const isValid = React.useMemo((): boolean => {
    /// make sure the user has selected a template
    if (isEmpty(selectedTemplate)) return false;
    /// make sure the user has selected a valid timeframe
    if (!isValidDateRange(timeframe)) return false;
    /// form is valid and can be submitted
    return true;
  }, [JSON.stringify(timeframe), JSON.stringify(selectedTemplate)]);

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>Block Report Settings</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        {loading && <LinearLoading />}
        <CardBody>
          <Row shouldCollapse style={{ height: '100%' }}>
            <LeftColumn>
              <TemplateSelect
                step={1}
                templates={templates}
                onSelect={templateChangeHandler}
                onDelete={templateDeleteHandler}
                selectedTemplateId={selectedTemplate?.id}
                formComponent={TemplateForm}
                reportType={'Block Report'}
                reportTypeId={'blockReport'}
              />
            </LeftColumn>
            <RightColumn>
              <TimeframeSelector
                onTimeframeChange={timeframeHandler}
                timeframe={timeframe}
                step={2}
              />
              <ToggleDiv>
                <Toggle
                  name='max-display-toggle'
                  label='Switch to use max session values, or off to use average from the session. A session is a single day.'
                  onChange={(): void =>
                    maxValueDatapointsToggled
                      ? setMaxValueDatapointsToggled(false)
                      : setMaxValueDatapointsToggled(true)
                  }
                  checked={maxValueDatapointsToggled}
                  value='datapoint-toggle'
                />
              </ToggleDiv>
              <div
                style={{
                  border: '1px solid var(--color-text)',
                  padding: '10px',
                  marginTop: '10px',
                }}
              >
                <p>
                  The Block Report displays the percent change in performance
                  between two single sessions for up to five metrics - it DOES
                  NOT consider all sessions within a range of dates.
                  <br></br>
                  <br></br>
                  We suggest using this report with at least one Key Performance
                  Indicator (KPI) metric in the first column and supporting
                  metrics in the remaining columns.<br></br>
                  <br></br>
                  Each column is filterable in ascending or descending order of
                  % change. You can choose to compare either the athlete&apos;s
                  best value within the two sessions OR the average from these
                  sessions. For example, if an athlete performs three tests on a
                  single day, the session will be an average of those three
                  jumps.
                  <br></br>
                  <br></br>
                  *Note that the max values for each metric may occur on
                  different individual tests during the chosen sessions. If an
                  athlete does not have a recorded test on the date(s) selected,
                  it will use the sessions closest to the beginning and end of
                  the specified date block.
                </p>
              </div>
              <p
                style={{
                  marginTop: '15px',
                  fontSize: '1.2em',
                  color: 'var(--color-accent)',
                }}
              >
                <a
                  rel='noreferrer'
                  target='_blank'
                  href='https://www.hawkindynamics.com/blog/applying-the-block-report-in-practice'
                >
                  https://www.hawkindynamics.com/blog/applying-the-block-report-in-practice
                </a>
              </p>
            </RightColumn>
          </Row>
        </CardBody>

        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

interface IProps extends RouteComponentProps {
  style?: React.CSSProperties;
  onSave: () => void;
}
export const BlockSettings = withRouter((props: IProps): React.ReactElement => {
  const open = (): void =>
    openPopup(
      <Popup onClose={closePopup(props.history)} onSave={props.onSave} />,
      props.history,
      'block-report-settings'
    );

  return <SettingsButton onClick={open} />;
});

const ToggleDiv = styled.div`
  * {
    margin: 2px 0;
  }
`;
